import React from 'react';
import AdminSpinner from './AdminSpinner';

/**
 * AdminButton
 * 
 * Standardized button component for admin pages.
 * 
 * @param {string} variant - Button variant: 'primary' | 'secondary' | 'destructive' | 'ghost'
 * @param {string} size - Button size: 'sm' | 'md' | 'lg' (default: 'md')
 * @param {boolean} disabled - Whether button is disabled
 * @param {boolean} loading - Whether button is in loading state
 * @param {string} children - Button text
 * @param {object} style - Additional styles
 * @param {string} type - Button type (default: 'button')
 * @param {string} ariaLabel - Aria label for accessibility
 * @param {function} onClick - Click handler
 * @param {object} ...props - Other button props
 */
export default function AdminButton({
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  children,
  style = {},
  type = 'button',
  ariaLabel,
  onClick,
  ...props
}) {
  const isDisabled = disabled || loading;

  const classNameMap = {
    primary: 'admin-button-primary',
    secondary: 'admin-button-secondary',
    destructive: 'admin-button-destructive',
    ghost: 'admin-button-ghost',
  };
  
  const sizeClassMap = {
    sm: 'admin-button-size-sm',
    md: 'admin-button-size-md',
    lg: 'admin-button-size-lg',
  };
  
  const variantClass = classNameMap[variant] || 'admin-button-primary';
  const sizeClass = sizeClassMap[size] || 'admin-button-size-md';
  const className = `${variantClass} ${sizeClass}`;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      aria-label={ariaLabel}
      className={className}
      style={{
        ...(isDisabled && { opacity: 0.6, cursor: 'not-allowed' }),
        ...style,
      }}
      {...props}
    >
      {loading ? (
        <>
          <AdminSpinner size="sm" color="currentColor" style={{ marginRight: 'var(--spacing-1)' }} />
          Loading...
        </>
      ) : children}
    </button>
  );
}

// Styles removed - now using CSS classes from admin-ui.css

